import React, { useContext, useEffect, useState } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import _ from 'lodash';
import { Button, FormControl, FormControlLabel, FormLabel, Radio, RadioGroup, Stack } from '@mui/material';
import { DateRangePicker } from '@mui/x-date-pickers-pro/DateRangePicker';
import { PickersShortcutsItem } from '@mui/x-date-pickers/PickersShortcuts';
import { DateRange } from '@mui/x-date-pickers-pro/models';

import { translate as t } from 'hooks/useTranslations';
import Modal from 'components/_layouts/Modal';
import { PolicyAcknowledgementsParams, UserStatusFilter } from '.';
import { FilterAlt } from '@mui/icons-material';
import UserPicker from 'components/_pickers/UserPicker';
import StatusPicker from './StatusPicker';
import { User } from 'views/App/Portal/LexID/Users/_types/user';
import { getUser } from 'views/App/Portal/LexID/Users/_data_sources/useOrganizationUser';
import { AppContext } from 'views/App/context';
import GroupPicker from 'components/_pickers/GroupPicker';
import ManualPicker from 'components/_pickers/ManualPicker';

export type UsersFiltersProps = {
  organizationId: string;
  params: PolicyAcknowledgementsParams;
  updateParams: (updatedParams: PolicyAcknowledgementsParams) => void;
};

export type CompleteOption = 'ALL' | 'YES' | 'NO';
dayjs.extend(quarterOfYear);

const UsersFilters: React.FC<UsersFiltersProps> = ({ organizationId, params, updateParams }: UsersFiltersProps) => {
  const { hasPermission, hasAppPermission } = useContext(AppContext);

  const [showFilters, setShowFilters] = useState(false);
  let initialComplete: CompleteOption = 'ALL';
  if (params.completed !== null) {
    initialComplete = params.completed?.toString() === 'true' ? 'YES' : 'NO';
  }
  const [complete, setComplete] = useState<CompleteOption>(initialComplete);
  const [status, setStatus] = useState<string>(params.status ?? '');
  const [manuals, setManuals] = useState<string[]>(params.manuals ?? []);
  const [users, setUsers] = useState<User[]>([]);
  const [groups, setGroups] = useState<string[]>(params.groups ?? []);
  const [userStatus, setUserStatus] = useState<UserStatusFilter>(params.userStatus ?? 'ACTIVE');
  const [assignedDateRange, setAssignedAtRange] = useState<DateRange<Dayjs> | null>(
    params.assignedDateStart || params.assignedDateEnd
      ? () => [dayjs(params.assignedDateStart), dayjs(params.assignedDateEnd)]
      : null
  );

  const handleApplyFilters = () => {
    const newParams: PolicyAcknowledgementsParams = {
      groups,
      users: _.isEmpty(users) ? null : users.map((u) => u.id!),
      manuals,
      status,
      userStatus,
    };
    if (assignedDateRange && assignedDateRange[0] && assignedDateRange[1]) {
      if (assignedDateRange[0].isAfter(assignedDateRange[1])) {
        return; // Show error message
      }
      newParams.assignedDateStart = assignedDateRange[0].format('YYYY-MM-DD');
      newParams.assignedDateEnd = assignedDateRange[1].format('YYYY-MM-DD');
    }
    if (complete !== 'ALL') {
      newParams.completed = complete === 'YES' ? 'true' : 'false';
    }
    updateParams(newParams);
    setShowFilters(false);
  };
  const shortcutsItems: PickersShortcutsItem<DateRange<Dayjs>>[] = [
    {
      label: t('reporting.policyAcknowledgments.dateRangeFilters.lastMonth'),
      getValue: () => {
        const today = dayjs();
        const startOfLastMonth = today.startOf('month').subtract(1, 'month');
        return [startOfLastMonth, startOfLastMonth.endOf('month')];
      },
    },
    {
      label: t('reporting.policyAcknowledgments.dateRangeFilters.lastQuarter'),
      getValue: () => {
        const today = dayjs();
        const startOfLastQuarter = today.startOf('quarter').subtract(1, 'quarter');
        return [startOfLastQuarter, startOfLastQuarter.endOf('quarter')];
      },
    },
    {
      label: t('reporting.policyAcknowledgments.dateRangeFilters.thisYear'),
      getValue: () => {
        const today = dayjs();
        const startOfLastMonth = today.startOf('year');
        return [startOfLastMonth, today];
      },
    },
    {
      label: t('reporting.policyAcknowledgments.dateRangeFilters.lastYear'),
      getValue: () => {
        const today = dayjs();
        const startOfLastYear = today.startOf('year').subtract(1, 'year');
        return [startOfLastYear, startOfLastYear.endOf('year')];
      },
    },
  ];

  const fetchUsers = async (ids: string[]) => {
    const userResults = await Promise.all(ids.map((id) => getUser(organizationId, id)));
    return userResults.filter(Boolean);
  };

  useEffect(() => {
    if (!_.isEmpty(params.users) && hasPermission('listUsers', organizationId)) {
      fetchUsers(params.users || []).then((data) => setUsers(data));
    }
  }, [params]);

  return (
    <>
      <Button
        onClick={() => setShowFilters(true)}
        size='small'
        variant='outlined'
        startIcon={<FilterAlt />}
      >
        {t('reporting.policyAcknowledgments.actions.filter')}
      </Button>
      <Modal
        title={`${t('reporting.policyAcknowledgments.filters.title')}`}
        size='sm'
        actions={
          <>
            <Button
              color='neutral'
              onClick={() => setShowFilters(false)}
            >
              {t('common.cancel')}
            </Button>
            <Button
              variant='contained'
              onClick={handleApplyFilters}
            >
              {t('reporting.policyAcknowledgments.actions.applyFilters')}
            </Button>
          </>
        }
        open={showFilters}
        onClose={() => setShowFilters(false)}
      >
        <Stack>
          {hasPermission('listGroups', organizationId) && (
            <FormControl>
              <FormLabel>{t('reporting.policyAcknowledgments.filters.groups')}</FormLabel>
              <GroupPicker
                id='groups_filter'
                organizationId={organizationId}
                value={groups ?? []}
                onChange={(value) => setGroups(value)}
                allowBlank
                sx={{ p: 0, paddingTop: 0 }}
              />
            </FormControl>
          )}
          {hasPermission('listUsers', organizationId) && (
            <FormControl>
              <FormLabel>{t('reporting.policyAcknowledgments.filters.users')}</FormLabel>
              <UserPicker
                id='users_filter'
                organizationId={organizationId}
                value={users ?? []}
                onChange={(value) => setUsers(value)}
                allowMultiple
                allowBlank
              />
            </FormControl>
          )}
          <FormControl>
            <FormLabel>{t('reporting.policyAcknowledgments.filters.userStatus')}</FormLabel>
            <RadioGroup
              row
              value={userStatus}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                setUserStatus(event.target.value as UserStatusFilter);
              }}
              sx={{
                '& .MuiRadio-root': {
                  padding: '2px 8px',
                },
              }}
            >
              <FormControlLabel
                value='ACTIVE'
                control={<Radio />}
                label={t('reporting.policyAcknowledgments.userStatus.ACTIVE')}
              />
              <FormControlLabel
                value='INACTIVE'
                control={<Radio />}
                label={t('reporting.policyAcknowledgments.userStatus.INACTIVE')}
              />
              <FormControlLabel
                value='ALL'
                control={<Radio />}
                label={t('reporting.policyAcknowledgments.userStatus.ALL')}
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>{t('reporting.policyAcknowledgments.filters.complete')}</FormLabel>
            <RadioGroup
              row
              value={complete}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) => {
                const newVal = event.target.value as CompleteOption;
                setComplete(newVal);
              }}
              sx={{
                '& .MuiRadio-root': {
                  padding: '2px 8px',
                },
              }}
            >
              <FormControlLabel
                key='complete_all'
                value='ALL'
                control={<Radio />}
                label={t('reporting.policyAcknowledgments.complete.ALL')}
              />
              <FormControlLabel
                key='complete_yes'
                value='YES'
                control={<Radio />}
                label={t('reporting.policyAcknowledgments.complete.YES')}
              />
              <FormControlLabel
                key='complete_no'
                value='NO'
                control={<Radio />}
                label={t('reporting.policyAcknowledgments.complete.NO')}
              />
            </RadioGroup>
          </FormControl>
          <FormControl>
            <FormLabel>{t('reporting.policyAcknowledgments.filters.status')}</FormLabel>
            <StatusPicker
              id='status_filter'
              value={_.isEmpty(status) ? [] : [status]}
              onChange={(value) => setStatus(value[0])}
              allowBlank
            />
          </FormControl>
          <FormControl>
            <FormLabel>{t('reporting.policyAcknowledgments.filters.assignedDate')}</FormLabel>
            <DateRangePicker
              value={assignedDateRange ?? undefined}
              onChange={(newValue) => setAssignedAtRange(newValue)}
              slotProps={{
                shortcuts: {
                  items: shortcutsItems,
                },
                actionBar: { actions: [] },
              }}
              sx={{
                '& .MuiFormControl-root': {
                  paddingTop: 0,
                },
                '& .MuiFormLabel-root': {
                  display: 'none',
                },
              }}
            />
          </FormControl>
          {hasPermission('listManualPermissions', organizationId) && hasAppPermission('listIssuedManuals') && (
            <FormControl>
              <FormLabel>{t('reporting.policyAcknowledgments.filters.manuals')}</FormLabel>
              <ManualPicker
                id='manuals_filter'
                organizationId={organizationId}
                value={manuals ?? []}
                onChange={(value) => setManuals(value)}
                allowBlank
                sx={{ p: 0, paddingTop: 0 }}
              />
            </FormControl>
          )}
        </Stack>
      </Modal>
    </>
  );
};

export default UsersFilters;
